import React from "react";
import { SearchIcon } from './Icons';
import { COLORS, LAYOUT, TYPOGRAPHY, CATEGORY_OPTIONS } from '../constants';

/**
 * Modern ListingFilters component with professional design
 * Reusable filter/search/sort UI for listings.
 */
function ListingFilters({
  filters,
  setFilters,
  search,
  setSearch,
  sort,
  setSort,
  showSearch = true,
  showSort = true,
}) {
  const containerStyle = {
    width: '100%',
    maxWidth: '1200px',
    margin: '0 auto 2rem auto',
    padding: '2rem',
    background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.primaryDark} 100%)`,
    borderRadius: '16px',
    boxShadow: '0 4px 16px rgba(206, 17, 38, 0.12)',
    border: 'none',
    position: 'relative',
    overflow: 'hidden',
  };

  const searchContainerStyle = {
    position: 'relative',
    maxWidth: '500px',
    margin: `0 auto ${LAYOUT.spacing.xl} auto`,
  };

  const searchInputStyle = {
    width: '100%',
    padding: '16px 20px 16px 50px',
    fontSize: '16px',
    border: '2px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '12px',
    outline: 'none',
    transition: 'all 0.3s ease',
    backgroundColor: COLORS.white,
    color: COLORS.text.primary,
    fontWeight: '500',
    boxShadow: 'none',
    '::placeholder': {
      color: COLORS.text.secondary,
    },
  };

  const searchIconStyle = {
    position: 'absolute',
    left: '18px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: COLORS.primary,
    pointerEvents: 'none',
    zIndex: 1,
  };

  const filtersGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: LAYOUT.spacing.lg,
    alignItems: 'end',
  };

  const inputGroupStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: LAYOUT.spacing.xs,
  };

  const labelStyle = {
    ...TYPOGRAPHY.caption,
    color: COLORS.white,
    fontWeight: '600',
    fontSize: '14px',
    marginBottom: '8px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
  };

  const inputStyle = {
    padding: '14px 18px',
    fontSize: '16px',
    border: '2px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '10px',
    outline: 'none',
    transition: 'all 0.3s ease',
    backgroundColor: COLORS.white,
    color: COLORS.text.primary,
    fontWeight: '500',
    boxShadow: 'none',
  };

  const selectStyle = {
    ...inputStyle,
    cursor: 'pointer',
    appearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23CE1126' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
    backgroundPosition: 'right 14px center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: '18px',
    paddingRight: '44px',
  };

  return (
    <div style={containerStyle}>
      {showSearch && (
        <div style={searchContainerStyle}>
          <div style={searchIconStyle}>
            <SearchIcon size={20} />
          </div>
          <input
            type="text"
            placeholder="Search by title, description, or keywords..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={searchInputStyle}
            onFocus={(e) => {
              e.target.style.borderColor = COLORS.white;
              e.target.style.backgroundColor = COLORS.white;
              e.target.style.boxShadow = '0 0 0 3px rgba(255, 255, 255, 0.25)';
              e.target.style.transform = 'translateY(-1px)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
              e.target.style.backgroundColor = COLORS.white;
              e.target.style.boxShadow = 'none';
              e.target.style.transform = 'translateY(0)';
            }}
          />
        </div>
      )}

      <div style={filtersGridStyle}>
        <div style={inputGroupStyle}>
          <label style={labelStyle}>Category</label>
          <select
            value={filters.category}
            onChange={(e) => setFilters((f) => ({ ...f, category: e.target.value }))}
            style={selectStyle}
            onFocus={(e) => {
              e.target.style.borderColor = COLORS.white;
              e.target.style.backgroundColor = COLORS.white;
              e.target.style.boxShadow = '0 0 0 3px rgba(255, 255, 255, 0.25)';
              e.target.style.transform = 'translateY(-1px)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
              e.target.style.backgroundColor = COLORS.white;
              e.target.style.boxShadow = 'none';
              e.target.style.transform = 'translateY(0)';
            }}
          >
            <option value="">All Categories</option>
            {CATEGORY_OPTIONS.map((option, index) => 
              option.isHeader ? (
                <option key={index} disabled style={{ 
                  fontWeight: 'bold', 
                  color: '#CE1126',
                  backgroundColor: '#f5f5f5'
                }}>
                  {option.label}
                </option>
              ) : (
                <option key={index} value={option.value}>
                  {option.label}
                </option>
              )
            )}
          </select>
        </div>

        <div style={inputGroupStyle}>
          <label style={labelStyle}>Min Price</label>
          <input
            type="number"
            placeholder="$0"
            min="0"
            value={filters.minPrice}
            onChange={(e) => {
              const value = e.target.value;
              // Don't allow negative values
              if (value === '' || parseFloat(value) >= 0) {
                setFilters((f) => ({ ...f, minPrice: value }));
              }
            }}
            onWheel={(e) => e.target.blur()} // Prevent scroll from changing value
            style={inputStyle}
            onFocus={(e) => {
              e.target.style.borderColor = COLORS.white;
              e.target.style.backgroundColor = COLORS.white;
              e.target.style.boxShadow = '0 0 0 3px rgba(255, 255, 255, 0.25)';
              e.target.style.transform = 'translateY(-1px)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
              e.target.style.backgroundColor = COLORS.white;
              e.target.style.boxShadow = 'none';
              e.target.style.transform = 'translateY(0)';
            }}
          />
        </div>

        <div style={inputGroupStyle}>
          <label style={labelStyle}>Max Price</label>
          <input
            type="number"
            placeholder="$999+"
            min="0"
            value={filters.maxPrice}
            onChange={(e) => {
              const value = e.target.value;
              // Allow user to type freely, just prevent negative values
              if (value === '' || parseFloat(value) >= 0) {
                setFilters((f) => ({ ...f, maxPrice: value }));
              }
            }}
            onWheel={(e) => e.target.blur()} // Prevent scroll from changing value
            style={inputStyle}
            onFocus={(e) => {
              e.target.style.borderColor = COLORS.white;
              e.target.style.backgroundColor = COLORS.white;
              e.target.style.boxShadow = '0 0 0 3px rgba(255, 255, 255, 0.25)';
              e.target.style.transform = 'translateY(-1px)';
            }}
            onBlur={(e) => {
              // Validate that max >= min when user finishes typing
              const value = e.target.value;
              const minPrice = parseFloat(filters.minPrice) || 0;
              const maxPrice = parseFloat(value);
              
              if (value !== '' && !isNaN(maxPrice) && maxPrice < minPrice) {
                // If max price is less than min price, set it to min price
                setFilters((f) => ({ ...f, maxPrice: minPrice.toString() }));
              }
              
              // Reset styles
              e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
              e.target.style.backgroundColor = COLORS.white;
              e.target.style.boxShadow = 'none';
              e.target.style.transform = 'translateY(0)';
            }}
          />
        </div>

        {showSort && (
          <div style={inputGroupStyle}>
            <label style={labelStyle}>Sort By</label>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              style={selectStyle}
              onFocus={(e) => {
                e.target.style.borderColor = COLORS.white;
                e.target.style.backgroundColor = COLORS.white;
                e.target.style.boxShadow = '0 0 0 3px rgba(255, 255, 255, 0.25)';
                e.target.style.transform = 'translateY(-1px)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                e.target.style.backgroundColor = COLORS.white;
                e.target.style.boxShadow = 'none';
                e.target.style.transform = 'translateY(0)';
              }}
            >
              <option value="newest">Newest First</option>
              <option value="priceLow">Price: Low to High</option>
              <option value="priceHigh">Price: High to Low</option>
              <option value="alphabetical">Alphabetical</option>
            </select>
          </div>
        )}
      </div>
    </div>
  );
}

export default ListingFilters;
